import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (type: ToastType, message: string, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (type: ToastType, message: string, duration = 4000) => {
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, type, message, duration }]);
      if (duration > 0) {
        setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      {/* Toast Container */}
      <div className="fixed bottom-6 right-6 z-[99999] flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        {toasts.map((toast) => {
          const config: Record<ToastType, { icon: string; bg: string; text: string }> = {
            success: { icon: 'ri-check-line', bg: 'bg-green-600', text: 'text-white' },
            error: { icon: 'ri-close-line', bg: 'bg-red-600', text: 'text-white' },
            info: { icon: 'ri-information-line', bg: 'bg-primary-600', text: 'text-white' },
            warning: { icon: 'ri-alert-line', bg: 'bg-accent-500', text: 'text-white' },
          };
          const c = config[toast.type];
          return (
            <div
              key={toast.id}
              className={`${c.bg} ${c.text} px-5 py-3.5 rounded-xl shadow-lg flex items-start gap-3 pointer-events-auto animate-slide-up`}
              role="alert"
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={c.icon}></i>
              </div>
              <p className="text-sm font-medium flex-1">{toast.message}</p>
              <button
                onClick={() => removeToast(toast.id)}
                className="w-5 h-5 flex items-center justify-center flex-shrink-0 opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                aria-label="Kapat"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}